// Imperative SVG drawing for the MDS perceptual map.
// Port of BrandPerceptialMapping.Mds.ChartView#render static parts (bpm/mds/chart.coffee),
// rewritten to the d3 v7 selection API (matches sibling React charts hm/vk/wc).
// The custom aytmTextCollisions layout (d3 v3 only) is reached separately via
// createTextCollisions() in phase 3 — not needed here.

import * as d3 from 'd3'

import { checkLineIntersection } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsProjection'
import { CLS, ID } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsClasses'

const ARROW_POLYLINE = '0,0 20,5 0,10 5,5'
const END_BUBBLE_RADIUS = 10
const END_INNER_RADIUS = END_BUBBLE_RADIUS / 2 + 2

// <b>/<i> → styled <tspan> (legacy format_text)
const formatText = text =>
  String(text)
    .replace(/<b>/g, '<tspan style="font-weight: bold;">')
    .replace(/<\/b>/g, '</tspan>')
    .replace(/<i>/g, '<tspan style="font-weight: italic;">')
    .replace(/<\/i>/g, '</tspan>')

const appendArrowMarker = (defs, id, fill) => {
  defs
    .append('marker')
    .attr('id', id)
    .attr('viewBox', '0 0 20 10')
    .attr('refX', 5)
    .attr('refY', 5)
    .attr('markerUnits', 'strokeWidth')
    .attr('orient', 'auto')
    .attr('markerWidth', 6)
    .attr('markerHeight', 3)
    .append('polyline')
    .attr('points', ARROW_POLYLINE)
    .attr('fill', fill)
}

// Path from the centre to the axis vector's intersection with the plot border.
const calcAxisPath = (d, projection, center) => {
  const borderX = projection.border.x
  const borderY = projection.border.y
  const line = { x1: center.xm, y1: center.ym, x2: d.x, y2: d.y }

  let borderLine = {}
  if (Math.sign(d.x) < 0) borderLine = { x1: -borderX, y1: borderY, x2: -borderX, y2: -borderY }
  if (Math.sign(d.x) > 0) borderLine = { x1: borderX, y1: borderY, x2: borderX, y2: -borderY }
  if (Math.sign(d.y) > 0) borderLine = { x1: -borderX, y1: borderY, x2: borderX, y2: borderY }
  if (Math.sign(d.y) < 0) borderLine = { x1: -borderX, y1: -borderY, x2: borderX, y2: -borderY }

  let intersection = checkLineIntersection(line, borderLine)
  const px = intersection.x
  const py = intersection.y

  // Re-clamp to whichever bound was overshot
  if (px < -borderX || px > borderX || py > borderY || py < -borderY) {
    borderLine = {}
    if (px < -borderX) borderLine = { x1: -borderX, y1: borderY, x2: -borderX, y2: -borderY }
    else if (px > borderX) borderLine = { x1: borderX, y1: borderY, x2: borderX, y2: -borderY }
    else if (py > borderY) borderLine = { x1: -borderX, y1: borderY, x2: borderX, y2: borderY }
    else if (py < -borderY) borderLine = { x1: -borderX, y1: -borderY, x2: borderX, y2: -borderY }
    intersection = checkLineIntersection(line, borderLine)
  }

  const sx = projection.scaleX
  const sy = projection.scaleY
  // Master drew nothing for an axis on the horizontal or the vertical, and the label cut then ran
  // against a zero-length path forever. The side alone decides the direction, so a left-hand label
  // still reads left to right; only an axis with no direction at all has no path.
  if (!Math.sign(d.x) && !Math.sign(d.y)) return null
  if (Math.sign(d.x) < 0) {
    return `M${sx(intersection.x)} ${sy(intersection.y)} L${sx(center.xm)} ${sy(center.ym)}`
  }
  return `M${sx(center.xm)} ${sy(center.ym)} L${sx(intersection.x)} ${sy(intersection.y)}`
}

const drawCenter = (selectedGroup, projection, center) => {
  const bublik = selectedGroup.append('g').classed(CLS.bublik, true)
  const cx = projection.scaleX(center.xm) || -100
  const cy = projection.scaleY(center.ym) || -100

  bublik
    .append('circle')
    .classed(CLS.centerOuter, true)
    .attr('cx', cx)
    .attr('cy', cy)
    .attr('r', 20)
    .attr('fill', '#DFDFDF')
    .attr('opacity', 0.8)
  bublik
    .append('circle')
    .classed(CLS.centerInner, true)
    .attr('cx', cx)
    .attr('cy', cy)
    .attr('r', 6)
    .attr('fill', '#FFFFFF')
}

const drawAxes = (axisesGroup, axises, projection, center) => {
  axises.forEach(d => {
    axisesGroup
      .append('path')
      .classed(CLS.axis, true)
      .attr('id', ID.line(d.x, d.y))
      .attr('d', calcAxisPath(d, projection, center))
      .attr('stroke', '#CCCCCC')
      .attr('stroke-width', '.5px')
  })
}

const drawArrowLines = (linesGroup, axises, projection, center) => {
  axises.forEach(d => {
    const g = linesGroup.append('g').classed(CLS.axisLines, true)
    const cx = Math.cos(d.a) * END_INNER_RADIUS + projection.scaleX(d.x)
    const cy = Math.sin(d.a) * -END_INNER_RADIUS + projection.scaleY(d.y)

    g.append('circle')
      .classed(CLS.endOuter, true)
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', END_BUBBLE_RADIUS)
      .attr('fill', '#E6E7E8')
    g.append('circle').classed(CLS.endInner, true).attr('cx', cx).attr('cy', cy).attr('r', 4).attr('fill', '#6CACE1')
    g.append('line')
      .classed(CLS.lines, true)
      .attr('x1', projection.scaleX(center.xm))
      .attr('y1', projection.scaleY(center.ym))
      .attr('x2', projection.scaleX(d.x))
      .attr('y2', projection.scaleY(d.y))
      .attr('stroke', '#333333')
      .attr('stroke-width', '1px')
  })
}

const drawPoints = (pointsGroup, points, projection, colors, chartLegendMode) => {
  points.forEach((d, i) => {
    const g = pointsGroup.append('g').classed(CLS.point, true).style('cursor', 'pointer').attr('id', d.id)
    const cx = projection.scaleX(d.x)
    const cy = projection.scaleY(d.y)

    g.append('circle')
      .classed(CLS.outer, true)
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 6)
      .attr('fill', colors[i % colors.length])
    g.append('circle').classed(CLS.inner, true).attr('cx', cx).attr('cy', cy).attr('r', 3).attr('fill', '#000000')

    // Label starts hidden off-screen; updateChartLabels positions it after measuring (phase 3)
    const text = g
      .append('g')
      .classed(CLS.label, true)
      .attr('transform', 'translate(-100, -100)')
      .append('text')
      .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
      .attr('font-size', '14px')
      .attr('line-height', '14px')
      .attr('fill', '#666')
      .attr('text-anchor', 'middle')
      .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
      .style('font-size', '16px')
      .style('line-height', '18px')
      .style('fill', '#666')
      .style('width', '100px')

    if (chartLegendMode === 'long') text.html(formatText(d.label.text))
    else if (chartLegendMode === 'short') text.html(formatText(d.val.replace('qv', 'E')))
  })
}

/**
 * Build the static MDS scene from scratch into `svgNode`.
 * Group/class names come from mdsClasses so later phases (labels, selection) target the same.
 */
export const drawStatic = (
  svgNode,
  { id, width, height, axises, points, projection, center, colors, chartLegendMode }
) => {
  const container = d3.select(svgNode)
  container.selectAll('*').remove()

  container.attr('width', width).attr('height', height).attr('viewbox', `0 0 ${width} ${height}`)

  container
    .append('rect')
    .attr('id', ID.bg(id))
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height)
    .attr('fill', '#ffffff')

  const defs = container.append('defs')
  appendArrowMarker(defs, ID.arrowDark(id), '#000000')
  appendArrowMarker(defs, ID.arrowGray(id), '#CCCCCC')

  const selectedGroup = container.append('g').classed(CLS.selected, true)
  const axisesGroup = container.append('g').classed(CLS.axises, true)
  const linesGroup = container.append('g').classed(CLS.lines, true)
  const pointsGroup = container.append('g').classed(CLS.points, true)

  drawCenter(selectedGroup, projection, center)
  drawAxes(axisesGroup, axises, projection, center)
  drawArrowLines(linesGroup, axises, projection, center)
  drawPoints(pointsGroup, points, projection, colors, chartLegendMode)
}
